import TooltipWrapper from '@/components/ui/tooltip';
import Share from '@/features/trade/components/share';
import {
  getCurrentAccount,
  useCurrentAccount,
} from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { cn } from '@/utils/cn';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatBigInt,
  formatNumber,
} from '@/utils/value-format';
import { useTranslation } from 'react-i18next';

type Props = {
  symbol: string;
};

export default function UnrealizedPnl({ symbol }: Props) {
  const { pnl, roePct, isLong, entryPrice, leverage } = useCurrentAccount(
    (state) => {
      const position = state.positions[symbol];
      return {
        pnl: position.unrealizedPnl.pnl.bigint,
        roePct: position.unrealizedPnl.roePct.decimal,
        entryPrice: position.size > 0n ? position.cost / position.size : 0n,
        isLong: position.isLong,
        leverage: position.initMarginRatio
          ? formatBigInt(10n ** 6n / position.initMarginRatio, 2n, {
              digits: 0,
              round: 'ceil',
            })
          : undefined,
      };
    },
  );

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'opacity-100',
          pnl < 0n && 'text-red',
          pnl > 0n && 'text-green',
        )}
      >
        {formatBigInt(pnl, COLLATERAL_DECIMALS, {
          digits: 2,
          showChange: true,
          style: 'currency',
        })}{' '}
        /{' '}
        {formatNumber(roePct, {
          digits: 2,
          style: 'percent',
          abs: true,
        })}
      </div>

      <Share
        onOpen={() => ({
          symbol,
          isLong,
          leverage,
          pnl: bigIntToDecimalStr(pnl, COLLATERAL_DECIMALS),
          roePct,
          entryPrice,
          isFilled: false,
        })}
      />
    </div>
  );
}

export function UnrealizedPnlHeader() {
  const { t } = useTranslation();
  return (
    <TooltipWrapper
      content={t('positionsTable.marketPnLTooltip')}
      className="text-wrap font-sans text-sm normal-case tracking-wide text-foreground"
      direction="bottom"
      offset={1.4}
    >
      <span className="underline">{t('positionsTable.pnl')}</span>
    </TooltipWrapper>
  );
}

export const unrealizedPnlAccessorFn = (symbol: string) => {
  const { unrealizedPnl } = getCurrentAccount(
    (state) => state.positions[symbol],
  );
  return bigIntToNum(unrealizedPnl.pnl.bigint, COLLATERAL_DECIMALS);
};
